<template>
  <div>
    <v-toolbar flat color="white">
      <v-btn icon @click="$router.go(-1)">
        <v-icon>arrow_back</v-icon>
      </v-btn>
      <v-toolbar-title>
        Mutabakat Detayları - {{ getTransactionTypeName(filterTransactionType) }}
      </v-toolbar-title>
      <v-spacer />
      <v-btn color="primary" @click="exportToExcel" :disabled="!filteredDetails || filteredDetails.length === 0">
        <v-icon left>file_download</v-icon>
        Excel'e Aktar
      </v-btn>
    </v-toolbar>

    <!-- Filtre Alanı -->
    <v-card class="ma-2 pa-3">
      <v-card-title class="pa-0 pb-2">
        <span class="subtitle-1">Filtre Alanı</span>
      </v-card-title>
      <v-layout wrap>
        <v-flex xs12 md3 class="px-1">
          <v-select
            v-model="filterTransactionType"
            :items="transactionTypeOptions"
            item-text="text"
            item-value="value"
            label="Ödeme Kalemi"
            clearable
            @change="applyFilters"
          />
        </v-flex>
        <v-flex xs12 md3 class="px-1">
          <v-select
            v-model="filterStatus"
            :items="statusOptions"
            item-text="text"
            item-value="value"
            label="Mutabakat Durumu"
            clearable
            @change="applyFilters"
          />
        </v-flex>
        <v-flex xs12 md3 class="px-1">
          <v-select
            v-model="filterOrderStatus"
            :items="orderStatusOptions"
            item-text="text"
            item-value="value"
            label="Sipariş Durumu"
            clearable
            @change="applyFilters"
          />
        </v-flex>
        <v-flex xs12 md3 class="px-1">
          <v-text-field
            v-model="searchOrderNumber"
            label="Sipariş No Ara"
            append-icon="search"
            clearable
            @input="applyFilters"
          />
        </v-flex>
      </v-layout>
    </v-card>

    <!-- Detay Tablosu -->
    <v-card class="ma-2">
      <v-data-table
        :headers="headers"
        :items="filteredDetails"
        :loading="$store.state.progress"
        :rows-per-page-items="[25, 50, 100]"
        class="elevation-0"
      >
        <template v-slot:items="props">
          <tr 
            :class="{ 'clickable-row': true }" 
            @click="showOrderBreakdown(props.item)"
          >
          <td>{{ props.item.orderNumber }}</td>
            <td>{{ props.item.sku || '-' }}</td>
            <td>{{ getTransactionTypeName(props.item.transactionType) }}</td>
          <td class="text-xs-right">{{ formatMoney(props.item.expectedAmount) }}</td>
          <td class="text-xs-right">{{ formatMoney(props.item.actualAmount) }}</td>
            <td class="text-xs-right" :class="getDifferenceClass(props.item.difference)">
            {{ formatMoney(props.item.difference) }}
          </td>
            <td class="text-xs-center">
            <v-chip 
              :color="getStatusColor(props.item.status)" 
              text-color="white" 
              small
            >
              {{ getStatusName(props.item.status) }}
            </v-chip>
          </td>
          <td>{{ getOrderStatusName(props.item.orderStatus) }}</td>
          </tr>
        </template>
        <template v-slot:no-data>
          <div class="text-xs-center pa-4">
            <p class="grey--text">Detay bulunamadı</p>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Sipariş Detay Dialog -->
    <v-dialog v-model="orderBreakdownDialog" max-width="900">
      <v-card>
        <v-card-title class="headline">
          <v-icon left>receipt</v-icon>
          Sipariş Detayı: {{ selectedOrder ? selectedOrder.orderNumber : '' }}
          <v-spacer />
          <span v-if="selectedOrder" class="body-2 grey--text">
            SKU: {{ selectedOrder.sku || '-' }}
          </span>
        </v-card-title>
        <v-card-text>
          <v-data-table
            :headers="breakdownHeaders"
            :items="orderBreakdownItems"
            hide-actions
            class="elevation-1"
          >
            <template v-slot:items="props">
              <tr>
                <td>{{ getTransactionTypeName(props.item.transactionType) }}</td>
                <td class="text-xs-right">{{ formatMoney(props.item.expectedAmount) }}</td>
                <td class="text-xs-right">{{ formatMoney(props.item.actualAmount) }}</td>
                <td class="text-xs-right" :class="getDifferenceClass(props.item.difference)">
                  {{ formatMoney(props.item.difference) }}
                </td>
                <td class="text-xs-center">
                  <v-chip 
                    :color="props.item.isMatched ? 'green' : 'red'" 
                    text-color="white" 
                    small
                  >
                    {{ props.item.isMatched ? 'Mutabık' : 'Mutabık değil' }}
                  </v-chip>
                </td>
              </tr>
            </template>
            <template v-slot:footer v-if="orderBreakdownItems && orderBreakdownItems.length > 0">
              <tr class="grey lighten-3 font-weight-bold">
                <td>Toplam</td>
                <td class="text-xs-right">{{ formatMoney(getBreakdownTotalExpected) }}</td>
                <td class="text-xs-right">{{ formatMoney(getBreakdownTotalActual) }}</td>
                <td class="text-xs-right" :class="getDifferenceClass(getBreakdownTotalDifference)">
                  {{ formatMoney(getBreakdownTotalDifference) }}
                </td>
                <td class="text-xs-center">
                  <v-chip 
                    :color="Math.abs(getBreakdownTotalDifference) < 0.01 ? 'green' : 'red'" 
                    text-color="white" 
                    small
                  >
                    {{ Math.abs(getBreakdownTotalDifference) < 0.01 ? 'Mutabık' : 'Mutabık değil' }}
                  </v-chip>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" flat @click="orderBreakdownDialog = false">Kapat</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import moment from 'moment';

export default {
  data() {
    return {
      filterTransactionType: null,
      filterStatus: null,
      filterOrderStatus: null,
      searchOrderNumber: '',
      
      // Dialog
      orderBreakdownDialog: false,
      selectedOrder: null,
      orderBreakdownItems: [],
      
      headers: [
        { text: 'Sipariş No', value: 'orderNumber' },
        { text: 'SKU', value: 'sku' },
        { text: 'Ödeme Kalemi', value: 'transactionType' },
        { text: 'Beklenen Tutar', value: 'expectedAmount', align: 'right' },
        { text: 'Gelen Tutar', value: 'actualAmount', align: 'right' },
        { text: 'Fark', value: 'difference', align: 'right' },
        { text: 'Durum', value: 'status', align: 'center' },
        { text: 'Sipariş Durumu', value: 'orderStatus' }
      ],
      breakdownHeaders: [
        { text: 'Tip', value: 'transactionType', sortable: false },
        { text: 'Beklenen Tutar', value: 'expectedAmount', align: 'right' },
        { text: 'Gelen Tutar', value: 'actualAmount', align: 'right' },
        { text: 'Fark', value: 'difference', align: 'right' },
        { text: 'Durum', value: 'status', align: 'center' }
      ],
      transactionTypeOptions: [
        { text: 'Tümü', value: null },
        { text: 'Hizmet Bedeli', value: 0 },
        { text: 'Sipariş Tutarı', value: 1 },
        { text: 'MP Stopaj', value: 2 },
        { text: 'Kargo Bedeli', value: 3 },
        { text: 'Komisyon', value: 4 },
        { text: 'İade Tutarı', value: 5 },
        { text: 'Stopaj İade', value: 6 },
        { text: 'Kampanya İndirim', value: 7 },
        { text: 'Komisyon İade', value: 8 },
        { text: 'Altın İşçilik Bedeli Stopaj', value: 9 },
        { text: 'Müşteri Memnuniyeti', value: 10 },
        { text: 'Sevkiyat Maliyeti Paylaşımı Gelir', value: 11 }
      ],
      statusOptions: [
        { text: 'Tümü', value: null },
        { text: 'Mutabık', value: 0 },
        { text: 'Eksik', value: 1 },
        { text: 'Mutabık Değil', value: 2 }
      ],
      orderStatusOptions: [
        { text: 'Tümü', value: null },
        { text: 'Normal', value: 0 },
        { text: 'İptal', value: 1 },
        { text: 'İade', value: 2 }
      ],
      
      // Query params from router
      tenantId: null,
      mpId: null,
      startDate: null,
      endDate: null
    };
  },
  computed: {
    ...mapGetters('reconciliation', ['details']),
    filteredDetails() {
      let filtered = this.details || [];
      
      if (this.filterTransactionType !== null) {
        filtered = filtered.filter(d => d.transactionType === this.filterTransactionType);
      }
      
      if (this.filterStatus !== null) {
        filtered = filtered.filter(d => d.status === this.filterStatus);
      }
      
      if (this.filterOrderStatus !== null) {
        filtered = filtered.filter(d => d.orderStatus === this.filterOrderStatus);
      }
      
      if (this.searchOrderNumber) {
        const search = this.searchOrderNumber.toLowerCase();
        filtered = filtered.filter(d => 
          d.orderNumber && d.orderNumber.toLowerCase().includes(search)
        );
      }
      
      return filtered;
    },
    getBreakdownTotalExpected() {
      return this.orderBreakdownItems.reduce((sum, item) => sum + (item.expectedAmount || 0), 0);
    },
    getBreakdownTotalActual() {
      return this.orderBreakdownItems.reduce((sum, item) => sum + (item.actualAmount || 0), 0);
    },
    getBreakdownTotalDifference() {
      return this.getBreakdownTotalActual - this.getBreakdownTotalExpected;
    }
  },
  methods: {
    ...mapActions('reconciliation', ['fetchDetails', 'fetchOrderBreakdown']),
    formatMoney(value) {
      if (value === null || value === undefined) return '0,00';
      return new Intl.NumberFormat('tr-TR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    },
    formatDateTime(date) {
      return moment(date).format('DD.MM.YYYY HH:mm');
    },
    getTransactionTypeName(value) {
      if (value === null || value === undefined) return 'Tümü';
      const option = this.transactionTypeOptions.find(opt => opt.value === value);
      return option ? option.text : '';
    },
    getStatusName(status) {
      const statusMap = {
        0: 'Mutabık',
        1: 'Eksik',
        2: 'Mutabık Değil'
      };
      return statusMap[status] || 'Bilinmeyen';
    },
    getStatusColor(status) {
      const colorMap = {
        0: 'green',
        1: 'orange',
        2: 'red'
      };
      return colorMap[status] || 'grey';
    },
    getOrderStatusName(value) {
      const option = this.orderStatusOptions.find(opt => opt.value === value);
      return option ? option.text : '';
    },
    getDifferenceClass(difference) {
      if (Math.abs(difference) < 0.01) return 'green--text';
      return difference > 0 ? 'green--text' : 'red--text';
    },
    applyFilters() {
      // Filters are applied in computed property
    },
    async showOrderBreakdown(detail) {
      this.selectedOrder = detail;
      this.orderBreakdownDialog = true;
      
      try {
        const breakdown = await this.$store.dispatch('reconciliation/fetchOrderBreakdown', {
          tenantId: this.tenantId,
          mpId: this.mpId,
          orderNumber: detail.orderNumber
        });
        this.orderBreakdownItems = breakdown || [];
      } catch (error) {
        console.error('Sipariş detay hatası:', error);
        this.orderBreakdownItems = [];
      }
    },
    exportToExcel() {
      // Excel export functionality
      this.$store.state.dialog = true;
      this.$store.state.message = 'Excel export özelliği yakında eklenecek.';
    },
    async loadData() {
      const query = this.$route.query;
      this.tenantId = query.tenantId ? parseInt(query.tenantId) : null;
      this.mpId = query.mpId ? parseInt(query.mpId) : null;
      this.startDate = query.startDate || null;
      this.endDate = query.endDate || null;
      this.filterTransactionType = query.transactionType !== undefined && query.transactionType !== null 
        ? parseInt(query.transactionType) 
        : null;

      if (this.tenantId && this.mpId) {
        await this.fetchDetails({
          tenantId: this.tenantId,
          mpId: this.mpId,
          startDate: this.startDate,
          endDate: this.endDate,
          transactionType: this.filterTransactionType
        });
      }
    }
  },
  async mounted() {
    await this.loadData();
  },
  watch: {
    '$route.query': {
      handler() {
        this.loadData();
      },
      deep: true
    }
  }
};
</script>

<style scoped>
.clickable-row {
  cursor: pointer;
}
.clickable-row:hover {
  background-color: #f5f5f5;
}
</style>
